import { setPaymentSystem, setPublicKeys } from './utils/storage';

import {
  addRecurlyDependencies,
  addStripeDependencies,
  addSolidgateDependencies,
  addPaddleDependencies,
  addTailoredpayCollectDependencies,
  addTailoredpayGatewayDependencies,
} from './utils/external-deps';
import { PaymentSystem, type TailoredpayConfig } from './constants';

export { default as Provider } from './components/provider';
export { default as CardForm } from './components/card-form';
export { default as CardElement } from './components/card-element';
export { ApplePayButton } from './components/apple-pay-button';
export { GooglePayButton } from './components/google-pay-button';
export { SolidgatePayPalButton, } from './solidgate/PayPal';
export { default as useApplePay } from './hooks/use-apple-pay';
export { useGooglePay } from './hooks/use-google-pay';
export { default as mappingPricesToProducts } from './utils/prices';
export { getPublicKeys } from './utils/storage';
export {
  PaymentSystem,
  ModeGooglePay,
  FOODZEE_BACKEND_SYSTEMS,
  isFoodzeeBackend,
  TailoredpayStatus,
  type SolidgatePayPalOrder,
  type SolidgateMercadoPagoOrder,
  type TailoredpayConfig,
  type TailoredpayThreeDSResult,
  type TailoredpayTokenizeResult,
} from './constants';
export type { ProductInfo } from './constants';
export {
  collectPaymentData,
  type TailoredpayPaymentData,
} from './tailoredpay/collect-payment-data';

/**
 * TailoredPay needs two scripts: Collect.js for the card fields and Gateway.js
 * for payer authentication. Gateway.js is only pulled in when the gateway has
 * 3DS enabled, and its failure must not take the card form down with it.
 */
/**
 * Gateway.js lives next to Collect.js on the gateway's own host, at
 * `/js/v1/Gateway.js` (verified against the TailoredPay gateway: it answers 200
 * and exports `get3DSecure`; `/token/Gateway.js` is a 404). The brief says both
 * script URLs come from /nmi/config, but the documented response only carries the
 * Collect.js one — so derive it and let the backend override once it sends its own.
 */
const deriveGatewayJsUrl = (collectJsUrl?: string) => {
  if (!collectJsUrl) {
    return undefined;
  }

  try {
    return new URL('/js/v1/Gateway.js', collectJsUrl).toString();
  } catch (e) {
    return undefined;
  }
};

const addTailoredpayDependencies = (config?: TailoredpayConfig) => {
  if (!config) {
    return Promise.reject(Error('TailoredPay config is missing'));
  }

  if (config.enabled === false) {
    return Promise.reject(
      Object.assign(Error('TailoredPay is not enabled for this project'), {
        code: 'tailoredpay_disabled',
      }),
    );
  }

  const deps: Promise<any>[] = [
    addTailoredpayCollectDependencies({
      collectJsUrl: config.collect_js_url,
      tokenizationKey: config.tokenization_key,
    }),
  ];

  const gatewayJsUrl = config.gateway_js_url || deriveGatewayJsUrl(config.collect_js_url);

  if (config.three_ds_enabled && gatewayJsUrl) {
    deps.push(
      addTailoredpayGatewayDependencies({ gatewayJsUrl })
        .catch((e: any) => {
          console.warn('[TailoredPay] Gateway.js failed to load — 3DS unavailable', e);
        }),
    );
  }

  return Promise.all(deps);
};

const ReactBilling = {
  async init(paymentSystem: PaymentSystem, keys: any) {
    try {
      if (!paymentSystem || !keys) {
        throw new Error('There is no paymentSystem or no keys');
      }

      // Check availability BEFORE claiming the module-level payment system:
      // `getPaymentSystem()` is what CardForm/CardElement branch on, so leaving
      // it pointing at a system we then failed to initialize would render a card
      // form whose SDK never loaded.
      if (
        paymentSystem === PaymentSystem.TAILOREDPAY
        && keys?.tailoredpay?.enabled === false
      ) {
        throw Object.assign(Error('TailoredPay is not enabled for this project'), {
          code: 'tailoredpay_disabled',
        });
      }

      setPaymentSystem(paymentSystem);
      setPublicKeys(keys);

      return Promise.all([
        paymentSystem === PaymentSystem.RECURLY
          ? addRecurlyDependencies()
          : paymentSystem === PaymentSystem.STRIPE
            ? addStripeDependencies()
            : paymentSystem === PaymentSystem.SOLIDGATE
              ? addSolidgateDependencies()
              : paymentSystem === PaymentSystem.PADDLE
                ? Promise.all([
                  addPaddleDependencies(),
                  addSolidgateDependencies()
                ])
                : paymentSystem === PaymentSystem.TAILOREDPAY
                  ? addTailoredpayDependencies(keys?.tailoredpay)
                  : Promise.reject(Error('Unsupported payment system')),
      ]);
    } catch (error) {
      console.log('[INIT ERROR]:', error);
      throw error;
    }
  },
};

export default ReactBilling;
